import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */

  
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
handleClick = async (event) => {
  event.preventDefault();

  const currentUrl = this.productPageUrl;
  if (!currentUrl) return;

  let cached = this.#cachedContent.get(currentUrl);

  if (!cached) {
    const response = await fetch(currentUrl);
    if (!response.ok) return;
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');

    const handle = new URL(currentUrl).pathname.split('/products/')[1]?.split('?')[0];
    let variantMediaMap = {};
    try {
      const jsonRes = await fetch(`/products/${handle}.js`);
      const productJson = await jsonRes.json();
      for (const variant of productJson.variants) {
        if (variant.featured_image?.src) {
          variantMediaMap[variant.id] = variant.featured_image.src;
        }
      }
    } catch(e) {}

    cached = {
      variantPicker: doc.querySelector('variant-picker')?.cloneNode(true),
      buyButtons: (
        doc.querySelector('.buy-buttons-block') ||
        doc.querySelector('buy-buttons') ||
        doc.querySelector('[data-type="add-to-cart-form"]')
      )?.cloneNode(true),
      variantMediaMap,
    };
    this.#cachedContent.set(currentUrl, cached);
  }

  const modalContent = document.getElementById('quick-add-modal-content');
  if (!modalContent) return;

  const productCard = this.closest('product-card');
  const cardImg = productCard?.querySelector('img');
  const cardTitle = productCard?.querySelector('a[href*="/products/"]')?.textContent?.trim();
  const cardPrice = productCard?.querySelector('product-price')?.cloneNode(true);

  const mediaDiv = document.createElement('div');
  mediaDiv.className = 'product-information__media';
  if (cardImg) {
    const imgClone = cardImg.cloneNode(true);
    imgClone.style.cssText = 'width:100%;height:100%;object-fit:cover;';
    imgClone.removeAttribute('loading');
    mediaDiv.appendChild(imgClone);
  }

  const detailsDiv = document.createElement('div');
  detailsDiv.className = 'product-details';

  if (cardTitle) {
    const titleDiv = document.createElement('div');
    titleDiv.className = 'view-product-title';
    titleDiv.innerHTML = `<a href="${currentUrl}">${cardTitle}</a>`;
    detailsDiv.appendChild(titleDiv);
  }

  if (cardPrice) detailsDiv.appendChild(cardPrice);
  if (cached.variantPicker) detailsDiv.appendChild(cached.variantPicker.cloneNode(true));
  if (cached.buyButtons) detailsDiv.appendChild(cached.buyButtons.cloneNode(true));

  modalContent.innerHTML = '';
  modalContent.appendChild(mediaDiv);
  modalContent.appendChild(detailsDiv);

  // Attach image swap listener - clean up previous one first
  const dialog = document.getElementById('quick-add-dialog');
  if (dialog) {
    // Abort previous listener
    dialog._imgSwapController?.abort();
    dialog._imgSwapController = new AbortController();

    dialog.addEventListener('change', (e) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

      const variantId = input.dataset.variantId;
      if (!variantId) return;

      const activeUrl = document.querySelector('quick-add-component[stay-visible]')?.productPageUrl;
      const activeCached = activeUrl ? this.#cachedContent.get(activeUrl) : null;
      const imgSrc = activeCached?.variantMediaMap?.[variantId];
      if (!imgSrc) return;

      const modalImg = document.querySelector('#quick-add-modal-content .product-information__media img');
      if (!modalImg) return;

      modalImg.style.transition = 'opacity 0.15s';
      modalImg.style.opacity = '0';
      setTimeout(() => {
        const optimizedSrc = imgSrc.includes('?') ? imgSrc + '&width=800' : imgSrc + '?width=800';
        modalImg.src = optimizedSrc;
        modalImg.srcset = '';
        modalImg.style.opacity = '1';
      }, 150);
    }, { signal: dialog._imgSwapController.signal });
  }


  // Preload all variant images for instant swap
  if (cached.variantMediaMap) {
    Object.values(cached.variantMediaMap).forEach(src => {
      const preloadSrc = src.includes('?') ? src + '&width=800' : src + '?width=800';
      const img = new Image();
      img.src = preloadSrc;
    });
  }

  this.#openQuickAddModal();
};








  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Updates the quick add modal with new product content
   * @param {Element} content - The product grid element
   */
updateQuickAddModal = async (content) => {
  const dialogComponent = document.getElementById('quick-add-dialog');
  if (!dialogComponent) return;

  const modalContent =
    dialogComponent.querySelector('#quick-add-modal-content') ||
    dialogComponent.querySelector('.quick-add-modal__content') ||
    dialogComponent.querySelector('[data-quick-add-content]');

  if (!modalContent) return;

  modalContent.innerHTML = '';
  modalContent.appendChild(content);
};


  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on cart update
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}